import httpClient from '@/httpClient/httpClient';
import {
  ResponseBody,
  RootState,
  CampaignState,
  CampaignInterface,
  Creative,
  ResponseDataProvider,
  DataProvider,
  Country,
  DropdownList,
  Ssp,
  OfferInterface,
  DeviceTypeInterface,
  Os,
  IChipsData,
  Region,
  City,
} from '@/types';
import { ActionTree, GetterTree, MutationTree } from 'vuex';
import ResponseDataValue, {
  ResponseDataProviderValue,
} from '@/utils/ResponseDataValue';
import { Notification } from 'uiv';
import i18n from '@/i18n';
import CreativeService from '@/services/CreativeService';

const REQUEST_ERROR = i18n.t('notifications.errorRequest');
const DELETE_SUCCESS = i18n.t('notifications.deleteSuccess');

const state: CampaignState = {
  campaign: null,
  campaigns: {
    paginationData: null,
    data: [] as Array<CampaignInterface>,
  },
  countries: {
    paginationData: null,
    data: [] as Array<Country>,
  },
  ssps: {
    paginationData: null,
    data: [] as Array<Country>,
  },
  langs: {
    paginationData: null,
    data: [] as Array<Country>,
  },
  os: {
    paginationData: null,
    data: [] as Array<Os>,
  },
  category: {
    paginationData: null,
    data: [] as Array<IChipsData>,
  },
  offers: {
    paginationData: null,
    data: [] as Array<OfferInterface>,
  },
  deviceType: {
    paginationData: null,
    data: [] as Array<OfferInterface>,
  },
  images: {
    paginationData: null,
    data: [] as Array<Creative>,
  },
  regions: {
    paginationData: null,
    data: [] as Array<Region>,
  },
  cities: {
    paginationData: null,
    data: [] as Array<City>,
  },
};
const mutations: MutationTree<CampaignState> = {
  setCampaign(state, data: CampaignInterface): void {
    state.campaign = data;
  },
  setCampaigns(state, data: DataProvider): void {
    if (!data.paginationData || !data.paginationData?.currentPage) {
      return;
    }
    state.campaigns.paginationData = data.paginationData;
    state.campaigns.data = data.data;
  },
  setCountries(state, data: DataProvider): void {
    if (!data.paginationData || !data.paginationData?.currentPage) {
      return;
    }
    state.countries.paginationData = data.paginationData;
    state.countries.data = data.data;
  },
  setSsps(state, data: DataProvider): void {
    if (!data.paginationData || !data.paginationData?.currentPage) {
      return;
    }
    state.ssps.paginationData = data.paginationData;
    state.ssps.data = data.data;
  },
  setLangs(state, data: DataProvider): void {
    if (!data.paginationData || !data.paginationData?.currentPage) {
      return;
    }
    state.langs.paginationData = data.paginationData;
    state.langs.data = data.data;
  },
  setOs(state, data: DataProvider): void {
    if (!data.paginationData || !data.paginationData?.currentPage) {
      return;
    }
    state.os.paginationData = data.paginationData;
    state.os.data = data.data;
  },
  setCategory(state, data: DataProvider): void {
    if (!data.paginationData || !data.paginationData?.currentPage) {
      return;
    }
    state.category.paginationData = data.paginationData;
    state.category.data = data.data;
  },
  setOffers(state, data: DataProvider): void {
    if (!data.paginationData || !data.paginationData?.currentPage) {
      return;
    }
    state.offers.paginationData = data.paginationData;
    state.offers.data = data.data;
  },
  setDeviceTypes(state, data: DataProvider): void {
    if (!data.paginationData || !data.paginationData?.currentPage) {
      return;
    }
    state.deviceType.paginationData = data.paginationData;
    state.deviceType.data = data.data;
  },
  setImages(state, data: DataProvider): void {
    if (!data.paginationData || !data.paginationData?.currentPage) {
      return;
    }
    state.images.paginationData = data.paginationData;
    if (+data.paginationData.currentPage == 1) {
      state.images.data = data.data;
    } else {
      state.images.paginationData = data.paginationData;
      data.data.map((item: Creative) => {
        state.images.data.push(item);
      });
    }
  },
  deleteCampaign(state, id: number) {
    state.campaigns.data = state.campaigns.data.filter(
      (item: CampaignInterface) => {
        return item.id != id;
      }
    );
  },
  setRegions(state, data: DataProvider): void {
    if (!data.paginationData || !data.paginationData?.currentPage) {
      return;
    }
    state.regions.paginationData = data.paginationData;
    state.regions.data = data.data;
  },
  setCities(state, data: DataProvider): void {
    if (!data.paginationData || !data.paginationData?.currentPage) {
      return;
    }
    state.cities.paginationData = data.paginationData;
    state.cities.data = data.data;
  },
};

const getters: GetterTree<CampaignState, RootState> = {
  campaign: (state) => {
    return state.campaign;
  },
  countriesList: (state) => {
    return state.countries.data.map((country: Country): DropdownList => {
      return {
        label: country.name,
        value: country.code,
      };
    });
  },
  sspList: (state) => {
    return state.ssps.data.map((ssp: Ssp): DropdownList => {
      return {
        label: ssp.name,
        value: ssp.id,
      };
    });
  },
  getSsp: (state) => {
    return state.ssps.data.map((s) => s);
  },
  langList: (state) => {
    return state.langs.data.map((country: Country): DropdownList => {
      return {
        label: country.name,
        value: country.code,
      };
    });
  },
  osList: (state) => {
    return state.os.data.map((os: Os): DropdownList => {
      return {
        label: os.name,
        value: os.id,
      };
    });
  },
  categoryList: (state) => {
    return state.category.data.map((category: IChipsData) => category);
  },
  offerList: (state) => {
    return state.offers.data.map((offer: OfferInterface): DropdownList => {
      return {
        label: offer.title,
        value: offer.id,
      };
    });
  },
  deviceTypeList: (state) => {
    return state.deviceType.data.map(
      (deviceType: DeviceTypeInterface): DropdownList => {
        return {
          label: deviceType.name,
          value: deviceType.value,
        };
      }
    );
  },
  regionsList: (state) => {
    return state.regions.data.map((region) => {
      return {
        label: region.name,
        value: region.code,
        country_code: region.country_code,
      };
    });
  },
  citiesList: (state) => {
    return state.cities.data.map((city) => {
      return {
        label: city.name,
        value: city.id,
        region_code: city.region_code,
      };
    });
  },
};

const actions: ActionTree<CampaignState, RootState> = {
  async campaign({ commit, dispatch }, data) {
    try {
      const response: ResponseBody = await httpClient().get(
        '/campaign/' + data.id,
        {
          params: data,
        }
      );

      commit('setCampaign', response.data);
      if (response.data?.creatives?.length) {
        CreativeService.addCreatives(response.data.creatives);
      }
      return ResponseDataValue(response.data, true);
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
  async countries({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().get(
        '/dictionary/country',
        {
          params: Object.assign({}, data),
        }
      );
      const responseDataProvider: ResponseDataProvider =
        ResponseDataProviderValue(response, true);
      commit('setCountries', responseDataProvider.data);
      return responseDataProvider;
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
  async ssps({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().get('/campaign/ssp', {
        params: Object.assign({}, data),
      });
      const responseDataProvider: ResponseDataProvider =
        ResponseDataProviderValue(response, true);
      commit('setSsps', responseDataProvider.data);
      return responseDataProvider;
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
  async langs({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().get(
        '/dictionary/language',
        {
          params: Object.assign({}, data),
        }
      );
      const responseDataProvider: ResponseDataProvider =
        ResponseDataProviderValue(response, true);
      commit('setLangs', responseDataProvider.data);
      return responseDataProvider;
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
  async os({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().get('/dictionary/os', {
        params: Object.assign({}, data, {
          per__page: 0,
        }),
      });
      const responseDataProvider: ResponseDataProvider =
        ResponseDataProviderValue(response, true);
      commit('setOs', responseDataProvider.data);
      return responseDataProvider;
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
  async categories({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().get(
        '/dictionary/category',
        {
          params: Object.assign({}, data, {
            per__page: 0,
          }),
        }
      );
      const responseDataProvider: ResponseDataProvider =
        ResponseDataProviderValue(response, true);
      commit('setCategory', responseDataProvider.data);
      return responseDataProvider;
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
  async offers({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().get('/campaign/offer', {
        params: Object.assign({}, data),
      });
      const responseDataProvider: ResponseDataProvider =
        ResponseDataProviderValue(response, true);
      commit('setOffers', responseDataProvider.data);
      return responseDataProvider;
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
  async deviceTypes({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().get(
        '/dictionary/device-type',
        {
          params: Object.assign({}, data),
        }
      );
      const responseDataProvider: ResponseDataProvider =
        ResponseDataProviderValue(response, true);
      commit('setDeviceTypes', responseDataProvider.data);
      return responseDataProvider;
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
  async campaigns({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().get('/campaign', {
        params: Object.assign({}, data.searchData, {
          expand: 'countries,screenshot,languages,geo,group',
          fields: data.columns.join(','),
        }),
      });
      const responseDataProvider: ResponseDataProvider =
        ResponseDataProviderValue(response, true);
      commit('setCampaigns', responseDataProvider.data);
      return responseDataProvider;
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
  async getImages({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().get(
        '/campaign/creative',
        {
          params: Object.assign({}, data, {
            is_group_file: 1,
            per__page: 42,
          }),
        }
      );
      const responseDataProvider: ResponseDataProvider =
        ResponseDataProviderValue(response, true);
      commit('setImages', responseDataProvider.data);
      return responseDataProvider;
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
  async createCampaign(_, formData) {
    try {
      const response: ResponseBody = await httpClient().post(
        '/campaign',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return ResponseDataValue(response.data, true);
    } catch (e) {
      return ResponseDataValue((e as any).response.data, false);
    }
  },
  async deleteCampaign({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().delete(
        '/campaign/' + data.id,
        {
          params: data,
        }
      );
      commit('deleteCampaign', data.id);
      Notification.notify.success({
        title: DELETE_SUCCESS,
      });
      return ResponseDataValue(response.data, true);
    } catch (e) {
      return ResponseDataValue((e as any).response.data, false);
    }
  },
  async updateCampaign(_, data) {
    try {
      const response: ResponseBody = await httpClient().put(
        '/campaign/' + data.id,
        data.formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return ResponseDataValue(response.data, true);
    } catch (e) {
      return ResponseDataValue((e as any).response.data, false);
    }
  },
  async cloneCampaign(_, data) {
    try {
      const response: ResponseBody = await httpClient().post(
        '/campaign/clone/' + data.id,
        data.formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return ResponseDataValue(response.data, true);
    } catch (e) {
      return ResponseDataValue((e as any).response.data, false);
    }
  },
  async changeStatusCampaign(_, data) {
    try {
      const response: ResponseBody = await httpClient().put(
        '/campaign/' + data.id,
        data.formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return ResponseDataValue(response.data, true);
    } catch (e) {
      return ResponseDataValue((e as any).response.data, false);
    }
  },
  async regions({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().get(
        '/dictionary/regions',
        {
          params: Object.assign({}, data),
        }
      );
      const responseDataProvider: ResponseDataProvider =
        ResponseDataProviderValue(response, true);
      commit('setRegions', responseDataProvider.data);
      return responseDataProvider;
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
  async loadCities({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().get(
        '/dictionary/cities',
        {
          params: Object.assign({}, data),
        }
      );
      const responseDataProvider: ResponseDataProvider =
        ResponseDataProviderValue(response, true);
      commit('setCities', responseDataProvider.data);
      return responseDataProvider;
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
  async bindCampaignsToGroup(_, formData) {
    try {
      const response: ResponseBody = await httpClient().put(
        '/campaign/bind-group',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return ResponseDataValue(response.data, true);
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
};

export default {
  state,
  mutations,
  getters,
  actions,
};
